import { Database } from "@/database.types"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"

type NewsContent = Database['public']['Tables']['news_contents']['Row']

// Define the structure for authoritative URLs
interface AuthoritativeUrl {
    url: string;
    title?: string;
}

// Define the structure for interesting links
interface InterestingLink {
    url: string;
    image?: string;
    title: string;
    author?: string;
    contents: string;
    publishedDate?: string;
}

interface NewsContentDialogProps {
    content: NewsContent | null
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function NewsContentDialog({ content, open, onOpenChange }: NewsContentDialogProps) {
    if (!content) return null

    const InfoSection = ({ title, value }: { title: string; value: string | number | null }) => (
        <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">{title}</h3>
            <p className="whitespace-pre-wrap break-words overflow-wrap-anywhere">{value || 'Not available'}</p>
        </div>
    )

    // Parse authoritative URLs from JSON if they exist
    const authoritativeUrls: AuthoritativeUrl[] = (() => {
        try {
            if (!content.authoritative_urls) return [];
            const urls = content.authoritative_urls as unknown;
            if (Array.isArray(urls)) {
                return urls.filter((item: any) => item && typeof item === 'object' && 'url' in item) as AuthoritativeUrl[];
            }
            return [];
        } catch (e) {
            console.error('Error parsing authoritative URLs:', e);
            return [];
        }
    })();

    // Parse interesting links from JSON if they exist
    const interestingLinks: InterestingLink[] = (() => {
        try {
            if (!content.interesting_links) return [];
            const links = content.interesting_links as unknown;
            if (Array.isArray(links)) {
                return links.filter((item: any) => item && typeof item === 'object' && 'url' in item) as InterestingLink[];
            }
            return [];
        } catch (e) {
            console.error('Error parsing interesting links:', e);
            return [];
        }
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="break-words overflow-wrap-anywhere">{content.raw_headline || content.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                    {/* Main Content Section */}
                    <div className="space-y-4">
                        <InfoSection title="Content" value={content.raw_content || content.description} />
                        {(content as any).contents && (
                            <InfoSection title="Contents" value={(content as any).contents} />
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <InfoSection title="Name" value={content.name} />
                            <InfoSection title="Primary Keyword" value={content.primary_keyword} />
                        </div>
                    </div>

                    {/* News Content Images */}
                    {(content as any).images && (
                        <div className="border-t pt-4">
                            <h2 className="text-lg font-semibold mb-3">News Content Images</h2>
                            <div className="space-y-2">
                                {(() => {
                                    try {
                                        const images = Array.isArray((content as any).images) 
                                            ? (content as any).images 
                                            : [];
                                        return images.map((imageUrl: any, index: number) => (
                                            <div key={index} className="border rounded p-3">
                                                <img 
                                                    src={typeof imageUrl === 'string' ? imageUrl : imageUrl?.url || imageUrl} 
                                                    alt={`News content image ${index + 1}`}
                                                    className="max-w-full h-auto rounded"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        ));
                                    } catch (e) {
                                        return <p className="text-sm text-muted-foreground">Error loading images</p>;
                                    }
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Trend Analysis Section */}
                    <div className="border-t pt-4">
                        <h2 className="text-lg font-semibold mb-3">Trend Analysis</h2>
                        <div className="space-y-4">
                            <InfoSection title="Trend" value={content.trend} />
                            <InfoSection title="Trend Analysis" value={content.trend_analysis} />
                            <div className="grid grid-cols-3 gap-4">
                                <InfoSection title="Longevity (Days)" value={content.longevity_in_days} />
                                <InfoSection title="Months Till Peak" value={content.months_till_peak} />
                                <InfoSection title="Total Duration (Months)" value={content.total_duration_in_months} />
                            </div>
                        </div>
                    </div>

                    {/* Business Analysis Section */}
                    <div className="border-t pt-4">
                        <h2 className="text-lg font-semibold mb-3">Business Analysis</h2>
                        <div className="space-y-4">
                            <InfoSection title="Audience and Brand Analysis" value={content.audience_and_brand_analysis} />
                            <InfoSection title="Business Opportunities" value={content.business_opportunities_analysis} />
                            <InfoSection title="Comparable Trends" value={content.comparable_trends} />
                        </div>
                    </div>

                    {/* Virality Section */}
                    <div className="border-t pt-4">
                        <h2 className="text-lg font-semibold mb-3">Virality Metrics</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <InfoSection title="Virality Score" value={content.virality_score} />
                            <InfoSection title="Virality Explanation" value={content.virality_explanation} />
                        </div>
                    </div>

                    {/* Metadata Section */}
                    <div className="border-t pt-4">
                        <h2 className="text-lg font-semibold mb-3">Metadata</h2>
                        <div className="grid grid-cols-3 gap-4">
                            <InfoSection title="Created" value={new Date(content.created_at).toLocaleString()} />
                            <InfoSection 
                                title="Updated" 
                                value={content.updated_at ? new Date(content.updated_at).toLocaleString() : 'Never'} 
                            />
                            <InfoSection title="Status" value={content.status} />
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <InfoSection title="Enrichment Status" value={content.enrichment_status} />
                            <InfoSection title="Source" value={content.custom_news_source_id} />
                        </div>
                    </div>

                    {/* Related Data Section */}
                    {content.related_news_and_data && (
                        <div className="border-t pt-4">
                            <h2 className="text-lg font-semibold mb-3">Related News and Data</h2>
                            <div className="bg-muted p-4 rounded-md whitespace-pre-wrap break-words overflow-wrap-anywhere font-mono text-sm">
                                {JSON.stringify(content.related_news_and_data, null, 2)}
                            </div>
                        </div>
                    )}

                    {/* Images Section */}
                    {content.images && (
                        <div className="border-t pt-4">
                            <h2 className="text-lg font-semibold mb-3">Images</h2>
                            <div className="bg-muted p-4 rounded-md whitespace-pre-wrap break-words overflow-wrap-anywhere font-mono text-sm">
                                {JSON.stringify(content.images, null, 2)}
                            </div>
                        </div>
                    )}

                    {/* Interesting Links Section */}
                    {interestingLinks.length > 0 && (
                        <div className="border-t pt-4">
                            <h2 className="text-lg font-semibold mb-3">Interesting Links</h2>
                            <div className="space-y-4">
                                {interestingLinks.map((item, index) => (
                                    <div key={index} className="bg-muted p-4 rounded-md break-words overflow-wrap-anywhere">
                                        {item.title && (
                                            <div className="font-medium mb-2 break-words overflow-wrap-anywhere text-lg">
                                                {item.title}
                                            </div>
                                        )}
                                        <div className="mb-2">
                                            <a 
                                                href={item.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="text-blue-600 hover:underline break-all overflow-wrap-anywhere text-sm"
                                            >
                                                {item.url}
                                            </a>
                                        </div>
                                        {item.author && (
                                            <div className="text-sm text-muted-foreground mb-2">
                                                Author: {item.author}
                                            </div>
                                        )}
                                        {item.publishedDate && (
                                            <div className="text-sm text-muted-foreground mb-2">
                                                Published: {new Date(item.publishedDate).toLocaleDateString()}
                                            </div>
                                        )}
                                        {item.contents && (
                                            <div className="text-sm mt-3 p-3 bg-background rounded border-l-2 border-primary/20">
                                                <div className="font-medium text-muted-foreground mb-1">Content:</div>
                                                <div className="whitespace-pre-wrap break-words overflow-wrap-anywhere">
                                                    {item.contents}
                                                </div>
                                            </div>
                                        )}
                                        {item.image && (
                                            <div className="mt-3">
                                                <img 
                                                    src={item.image} 
                                                    alt={item.title || 'Content image'} 
                                                    className="max-w-full h-auto rounded border"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Authoritative URLs Section */}
                    {authoritativeUrls.length > 0 && (
                        <div className="border-t pt-4">
                            <h2 className="text-lg font-semibold mb-3">Authoritative URLs</h2>
                            <div className="space-y-2">
                                {authoritativeUrls.map((item, index) => (
                                    <div key={index} className="bg-muted p-3 rounded-md break-words overflow-wrap-anywhere">
                                        {item.title && <div className="font-medium mb-1 break-words overflow-wrap-anywhere">{item.title}</div>}
                                        <a 
                                            href={item.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="text-blue-600 hover:underline break-all overflow-wrap-anywhere"
                                        >
                                            {item.url}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
} 